import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { AlertModal, ConfirmModal } from '../components/Modal';

const DialogContext = createContext(null);

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog must be used within a DialogProvider');
  return ctx;
}

export function DialogProvider({ children }) {
  // Alert state
  const [alertState, setAlertState] = useState(null);
  // Confirm state
  const [confirmState, setConfirmState] = useState(null);

  // Ref to hold the resolve function for confirm dialogs
  const confirmResolveRef = useRef(null);

  // `theme` is the room it is shown in: 'light' is the app's white card, 'admin' is the
  // staff panel's radius-28 modal. Same promise either way.
  const showAlert = useCallback(({ title, message, type = 'info', theme = 'light' }) => {
    return new Promise((resolve) => {
      setAlertState({ title, message, type, theme, onClose: () => { setAlertState(null); resolve(); } });
    });
  }, []);

  const showConfirm = useCallback(({
    title = 'CONFIRM ACTION',
    message,
    confirmLabel = 'CONFIRM',
    cancelLabel = 'CANCEL',
    variant = 'danger',
    // 'light' is the app's white card; 'portal' is the black panel the Portal page uses;
    // 'admin' is the staff panel's white radius-28 modal.
    theme = 'light',
    // A third button between cancel and confirm. Resolves 'second', so a caller that
    // asked for one gets three answers: true · 'second' · false.
    secondLabel,
  }) => {
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmState({
        title,
        message,
        confirmLabel,
        cancelLabel,
        variant,
        theme,
        secondLabel,
      });
    });
  }, []);

  const handleSecond = useCallback(() => {
    confirmResolveRef.current?.('second');
    confirmResolveRef.current = null;
    setConfirmState(null);
  }, []);

  const handleConfirm = useCallback(() => {
    confirmResolveRef.current?.(true);
    confirmResolveRef.current = null;
    setConfirmState(null);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    confirmResolveRef.current?.(false);
    confirmResolveRef.current = null;
    setConfirmState(null);
  }, []);

  return (
    <DialogContext.Provider value={{ showAlert, showConfirm }}>
      {children}

      {/* Alert Modal */}
      {alertState && (
        <AlertModal
          isOpen={true}
          onClose={alertState.onClose}
          title={alertState.title}
          message={alertState.message}
          type={alertState.type}
          theme={alertState.theme}
        />
      )}

      {/* Confirm Modal */}
      {confirmState && (
        <ConfirmModal
          isOpen={true}
          onClose={handleConfirmCancel}
          onConfirm={handleConfirm}
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          cancelLabel={confirmState.cancelLabel}
          variant={confirmState.variant}
          theme={confirmState.theme}
          secondLabel={confirmState.secondLabel}
          onSecond={handleSecond}
        />
      )}
    </DialogContext.Provider>
  );
}

export default DialogContext;
